import { Stack, Box, List, Input, IconButton, Checkbox } from '@chakra-ui/react'
import { ChevronUpIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons'
import _filter from 'lodash/filter'
import _union from 'lodash/union'
import _findIndex from 'lodash/findIndex'
import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { routes } from 'src/lib/routes'
import { useHotkeys } from 'react-hotkeys-hook'
import hotkeys from 'src/lib/hotkeys'

import { motion } from 'framer-motion'
import { gql } from '@apollo/client'
const MotionStack = motion(Stack)
import CellLoading from 'src/components/CellLoading'
import SchoolLine from 'src/components/SchoolListCell/SchoolLine'

export const QUERY = gql`
  query {
    schools {
      id
      name: schoolName
      subdomain
      usedForTesting
      acronym
      dormant
    }
  }
`

export const beforeQuery = (props) => {
  return { variables: props, fetchPolicy: 'cache-first' }
}

export const Success = ({ selected, schools, loading = false }) => {
  const [searchParams] = useSearchParams()
  const search = searchParams.get('search')
  const navigate = useNavigate()
  const [query, setQuery] = useState(search || '')
  const [advance, setAdvance] = useState(false)
  const [showDormant, setShowDormant] = useState(false)
  const searchInput = useRef(null)

  // React Router passes :id as a string; GraphQL returns numeric ids — match original strict equality behavior.
  const selectedId =
    selected != null && selected !== ''
      ? Number(selected)
      : null
  const selectedSchoolId =
    selectedId != null && !Number.isNaN(selectedId) ? selectedId : null

  useEffect(() => {
    setQuery(search || '')
  }, [search])

  const handleChange = (e) => {
    setQuery(e.target.value.toLowerCase())
  }
  let filtered = query
    ? _union(
      _filter(schools, (s) => s.name?.toLowerCase().match(query)),
      _filter(schools, (s) => s.subdomain?.toLowerCase().match(query)),
      _filter(schools, (s) => s.acronym?.toLowerCase().match(query)),
      _filter(schools, (s) => s.id == query)
    )
    : schools
  if (showDormant) {
    filtered = _filter(filtered, (s) => s.dormant)
  } else {
    filtered = _filter(filtered, (s) => !s.dormant)
  }
  useHotkeys(hotkeys.searchInput, () => searchInput.current.focus(), {
    keyup: true,
  })

  const handleSelectSchool = (school) => {
    if (query) {
      navigate(
        `${routes.school({ id: school.id })}?search=${encodeURIComponent(
          query
        )}`
      )
    } else {
      navigate(routes.school({ id: school.id }))
    }
  }

  const navigateList = (moveUp = false) => {
    const currentSchoolIndex = _findIndex(
      filtered,
      (s) => s.id === selectedSchoolId
    )
    const nextIdx = moveUp ? currentSchoolIndex - 1 : currentSchoolIndex + 1
    const nextSchool = filtered[nextIdx] || filtered[0]
    handleSelectSchool(nextSchool)
  }

  useHotkeys(hotkeys.down, () => navigateList(), { enableOnTags: ['INPUT'] })
  useHotkeys(hotkeys.up, () => navigateList(true), { enableOnTags: ['INPUT'] })

  return (
    <>
      <Stack isInline spacing={0}>
        <IconButton
          aria-label="Expand search"
          icon={advance ? <ChevronUpIcon /> : <ChevronDownIcon />}
          onClick={() => {
            setAdvance(!advance)
            searchInput.current.focus()
          }}
        />
        <Input
          ref={searchInput}
          autoFocus={true} // eslint-disable-line jsx-a11y/no-autofocus
          value={query}
          placeholder={loading ? 'Loading...' : 'Search clients [ / ]'}
          onChange={handleChange}
        />
        {query ? (
          <IconButton
            aria-label="Clear search"
            icon={<CloseIcon />}
            onClick={() => {
              searchInput.current.focus()
              setQuery('')
            }}
          />
        ) : null}
      </Stack>
      {advance ? (
        <MotionStack
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          padding={3}
          spacing={3}
          border="1px solid #eee"
        >
          <Checkbox
            onChange={() => setShowDormant(!showDormant)}
            isChecked={showDormant}
          >
            Show dormant schools
          </Checkbox>
        </MotionStack>
      ) : null}
      <Box
        height={advance ? 'calc(100vh - 10rem)' : 'calc(100vh - 8rem)'}
        overflowY="auto"
      >
        <List>
          {filtered.map((school) => (
            <SchoolLine
              selected={selectedSchoolId === school.id}
              key={school.id}
              school={school}
              handleClick={() => {
                handleSelectSchool(school)
              }}
            />
          ))}
        </List>
      </Box>
    </>
  )
}

export const Loading = CellLoading

export const Empty = () => <div>Empty</div>

export const Failure = (props) => {
  return <div>Error: {props.error.message}</div>
}
